import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import { sendSkiltbestilling } from '../../utils/api';
import {
    skiltbestillingsteg,
    useSkiltbestillingState,
} from './Skiltbestilling';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { Stegveiviser } from '../../components/Stegveiviser';
import { Skiltvisning } from './internals/Skiltvisning';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import {
    SVVButton,
    SVVButtonCancel,
    SVVButtonRow,
} from '@komponentkassen/svv-button';

export const SendBestilling: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    const { hent, data, laster } = useApi(sendSkiltbestilling);

    const { handleSubmit } = useForm();

    const rettighetId = useSkiltbestillingState((state) => state.rettighetId);
    const kjennemerkestorrelser = useSkiltbestillingState(
        (state) => state.kjennemerkestorrelser,
    );
    const kjennemerketype = useSkiltbestillingState(
        (state) => state.kjennemerketype,
    );
    const tegnkombinasjon = useSkiltbestillingState(
        (state) => state.tegnkombinasjon,
    );
    const trafikkstasjon = useSkiltbestillingState(
        (state) => state.trafikkstasjon,
    );
    const setBestillingDato = useSkiltbestillingState(
        (state) => state.setBestillingDato,
    );
    const varselmetode = useSkiltbestillingState((state) => state.varselmetode);
    const varselKontaktinfo = useSkiltbestillingState(
        (state) => state.varselKontaktinfo,
    );

    useEffect(() => {
        if (data?.ok) {
            setBestillingDato(data.dato);
            navigate('/skiltbestilling/bekreftelse');
        }
    }, [data, navigate, setBestillingDato]);

    if (!varselmetode || !varselKontaktinfo) {
        return <Navigate to="/" />;
    }

    const kjennemerkestorrelserListe = [
        kjennemerkestorrelser.foran,
        kjennemerkestorrelser.bak,
    ].filter((km) => km);

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1 className="spacing-bottom--medium">
                    {t('personlig-kjennemerke.bestill.overskrift')}
                </h1>
            </SVVLayoutBanner>

            <SVVLayoutBanner secondary>
                <form
                    className="form-group"
                    onSubmit={handleSubmit(() => {
                        hent({
                            rettighetId,
                            trafikkstasjon,
                            kjennemerkeStorrelse: kjennemerkestorrelserListe,
                            kjennemerkeType: kjennemerketype,
                            varselmetode: varselmetode,
                            varselKontaktinfo: varselKontaktinfo,
                        });
                    })}
                >
                    <div className="form-layout spacing-bottom--medium">
                        <div className="form-layout__nav">
                            <Stegveiviser
                                steg={skiltbestillingsteg}
                                aktivtSteg={5}
                            />
                        </div>
                        <div className="form-layout__main">
                            <h2 className="spacing-bottom--xsmall">
                                {t(
                                    'personlig-kjennemerke.bestill.steg.bekreft',
                                )}
                            </h2>
                            <p className="spacing-bottom--small">
                                {t(
                                    'personlig-kjennemerke.bestill.oppsummering.overskrift',
                                )}
                            </p>
                            <div className="spacing-bottom--medium">
                                <Skiltvisning
                                    modus="detaljer"
                                    kjennemerkestorrelser={
                                        kjennemerkestorrelser
                                    }
                                    kjennemerketype={kjennemerketype}
                                    tegnkombinasjon={tegnkombinasjon}
                                />
                            </div>

                            <p className="svv-h5 svv-h5--spacing">
                                {t(
                                    'personlig-kjennemerke.bestill.oppsummering.trafikkstasjon',
                                )}
                            </p>
                            <p>
                                {t(
                                    'personlig-kjennemerke.bestill.oppsummering.hentested.tekst.del1',
                                )}{' '}
                                <strong>{trafikkstasjon?.navn}</strong>{' '}
                                {t(
                                    'personlig-kjennemerke.bestill.oppsummering.hentested.tekst.del2',
                                )}
                            </p>
                        </div>
                    </div>
                    <div aria-live="polite">
                        {laster && (
                            <SVVSpinner size="small" centered={false}>
                                {t('personlig-kjennemerke.delt.laster')}
                            </SVVSpinner>
                        )}
                    </div>
                    {!laster && (
                        <SVVButtonRow>
                            <SVVButton type="submit">
                                {t(
                                    'personlig-kjennemerke.bestill.oppsummering.bestill-skilt',
                                )}
                            </SVVButton>
                            <SVVButton
                                onClick={() => navigate(-1)}
                                color="secondary"
                            >
                                {t('personlig-kjennemerke.delt.tilbake')}
                            </SVVButton>
                            <SVVButtonCancel href={`#status/${rettighetId}`}>
                                {t('personlig-kjennemerke.delt.avbryt')}
                            </SVVButtonCancel>
                        </SVVButtonRow>
                    )}
                </form>
            </SVVLayoutBanner>
        </>
    );
};
